import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { OriginBadge, TypeTag, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Check, X, Pencil, Copy, Clock, Archive, ArrowUpCircle, AlertTriangle, Radar, Loader2 } from "lucide-react";

const FILTERS = ["UNREVIEWED", "ACCEPTED", "REJECTED", "MODIFIED", "PROMOTED", "HISTORICAL", "SUPERSEDED", "DUPLICATE"];

export default function ClaimReview() {
  const [filter, setFilter] = useState("UNREVIEWED");
  const [claims, setClaims] = useState([]);
  const [promote, setPromote] = useState(null);
  const [conflicts, setConflicts] = useState({}); // claimId -> result
  const [checking, setChecking] = useState(null);

  const load = useCallback(() => api.get(`/claims?review_status=${filter}`).then((r) => setClaims(r.data)), [filter]);
  useEffect(() => { load(); }, [load]);

  const checkConflicts = async (id) => {
    setChecking(id);
    try {
      const { data } = await api.post(`/claims/${id}/check-conflicts`, {});
      setConflicts((c) => ({ ...c, [id]: data }));
      toast[data.has_conflicts ? "warning" : "success"](
        data.has_conflicts ? `${data.conflicts.length} conflict(s) with existing knowledge.` : "No contradictions found."
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Conflict check failed");
    } finally {
      setChecking(null);
    }
  };

  const act = async (id, body) => {
    try {
      await api.post(`/claims/${id}/review`, body);
      toast.success("Claim reviewed. Provenance preserved.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div>
      <PageHeader title="Claim Review" subtitle="Candidate claims stay UNREVIEWED until you act. An AI-generated idea never becomes doctrine on its own." />
      <div className="p-10">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="claim-filters">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border rounded-sm ${filter === f ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-500 hover:border-slate-500"}`}>{f}</button>
          ))}
        </div>

        <div className="space-y-4" data-testid="claims-list">
          {claims.length === 0 && <div className="text-slate-400 text-sm border border-slate-200 bg-white p-8">No {filter.toLowerCase()} claims.</div>}
          {claims.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 p-6" data-testid={`claim-${c.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <OriginBadge origin={c.claim_origin} />
                <TypeTag type={c.claim_type} />
                {c.existing_relationship && (
                  <span className="font-mono text-[10px] uppercase tracking-wider border border-slate-300 bg-slate-50 text-slate-600 px-1.5 py-0.5 rounded-sm" data-testid={`relationship-${c.id}`}>
                    {c.existing_relationship.replace(/_/g, " ")}
                  </span>
                )}
                {c.founder_acceptance && (
                  <span className="font-mono text-[10px] uppercase tracking-wider border border-slate-900 bg-slate-900 text-white px-1.5 py-0.5 rounded-sm" data-testid={`founder-accepted-${c.id}`}>
                    founder-accepted (origin still {c.claim_origin})
                  </span>
                )}
                <span className="font-mono text-[10px] text-slate-400 uppercase">extracted by {c.extracted_by} · conf {Math.round((c.confidence || 0) * 100)}%</span>
                {c.source_family && <span className="font-mono text-[10px] text-slate-400 ml-auto">{c.source_family}</span>}
              </div>
              <p className="text-slate-800 leading-relaxed">{c.claim_text}</p>
              {c.related_ids?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-[10px] uppercase text-slate-400">Related:</span>
                  {c.related_ids.map((id) => <span key={id} className="font-mono text-xs bg-slate-100 text-slate-700 px-1.5 rounded-sm">{id}</span>)}
                </div>
              )}
              {c.founder_acceptance?.acceptance_excerpt && (
                <p className="text-xs text-slate-500 mt-1 border-l-2 border-slate-900 pl-3">Founder acceptance (separate evidence): “{c.founder_acceptance.acceptance_excerpt}” <span className="text-slate-400">[{c.founder_acceptance.acceptance_location}]</span></p>
              )}
              {c.evidence_excerpt && <p className="text-xs text-slate-500 mt-2 border-l-2 border-slate-200 pl-3 italic">“{c.evidence_excerpt}”</p>}
              <div className="text-xs text-slate-400 font-mono mt-2">from {c.source_title} · {c.source_location}</div>

              {filter === "UNREVIEWED" && (
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => checkConflicts(c.id)} disabled={checking === c.id} className="rounded-sm gap-1.5 border-slate-400" data-testid={`check-conflicts-${c.id}`}>
                    {checking === c.id ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />} Contradiction Radar
                  </Button>
                  <Button size="sm" onClick={() => act(c.id, { action: "ACCEPT" })} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid={`accept-${c.id}`}><Check size={14} /> Accept</Button>
                  <Button size="sm" onClick={() => setPromote({ claim: c, conflict: conflicts[c.id] })} className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid={`promote-${c.id}`}><ArrowUpCircle size={14} /> Promote to Knowledge</Button>
                  <Button size="sm" variant="outline" onClick={() => { const t = prompt("Modified claim text:", c.claim_text); if (t) act(c.id, { action: "MODIFY", modified_text: t }); }} className="rounded-sm gap-1.5" data-testid={`modify-${c.id}`}><Pencil size={14} /> Modify</Button>
                  <Button size="sm" variant="outline" onClick={() => act(c.id, { action: "REJECT" })} className="rounded-sm gap-1.5 text-red-700" data-testid={`reject-${c.id}`}><X size={14} /> Reject</Button>
                  <Button size="sm" variant="outline" onClick={() => act(c.id, { action: "DUPLICATE" })} className="rounded-sm gap-1.5" data-testid={`duplicate-${c.id}`}><Copy size={14} /> Duplicate</Button>
                  <Button size="sm" variant="outline" onClick={() => act(c.id, { action: "HISTORICAL" })} className="rounded-sm gap-1.5" data-testid={`historical-${c.id}`}><Clock size={14} /> Historical</Button>
                  <Button size="sm" variant="outline" onClick={() => act(c.id, { action: "SUPERSEDED" })} className="rounded-sm gap-1.5" data-testid={`superseded-${c.id}`}><Archive size={14} /> Superseded</Button>
                </div>
              )}
              {conflicts[c.id] && <ConflictPanel result={conflicts[c.id]} testid={`conflict-panel-${c.id}`} />}
              {c.promoted_knowledge_item_id && <div className="mt-3 text-xs font-mono text-emerald-700">Promoted to structured knowledge</div>}
            </div>
          ))}
        </div>
      </div>

      <PromoteDialog entry={promote} onClose={() => setPromote(null)} onDone={() => { setPromote(null); load(); }} />
    </div>
  );
}

function ConflictPanel({ result, testid }) {
  if (!result) return null;
  if (!result.has_conflicts) {
    return (
      <div data-testid={testid} className="mt-4 border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 rounded-sm text-sm flex items-center gap-2">
        <Radar size={15} /> Contradiction Radar found no conflict with existing CANONICAL or WORKING knowledge.
      </div>
    );
  }
  return (
    <div data-testid={testid} className="mt-4 border border-red-200 bg-red-50/70 rounded-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-red-200 text-red-800">
        <AlertTriangle size={15} />
        <span className="font-mono text-[11px] uppercase tracking-wider">Contradiction Radar — {result.conflicts.length} conflict(s)</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="text-xs text-slate-600">
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Candidate</span>
          <div className="mt-0.5 flex items-center gap-2">
            <OriginBadge origin={result.candidate_origin} />
            <span className="font-mono text-[10px] text-slate-400">{result.candidate_status}</span>
          </div>
          <p className="mt-1 text-slate-700">“{result.candidate_text}”</p>
        </div>
        {result.conflicts.map((c) => (
          <div key={c.knowledge_id} className="border-l-2 border-red-400 pl-3" data-testid={`conflict-${c.knowledge_id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs text-slate-700 bg-white border border-slate-200 px-1.5 rounded-sm">{c.knowledge_id}</span>
              <StatusBadge status={c.existing_status} />
              <span className="font-mono text-[10px] text-slate-400">authority {c.existing_authority}</span>
            </div>
            <p className="text-sm text-slate-700"><span className="text-slate-400">Existing:</span> {c.existing_statement}</p>
            <p className="text-xs text-red-700 mt-1"><span className="font-mono uppercase text-[10px]">Why conflict:</span> {c.conflict_reason}</p>
          </div>
        ))}
        <p className="text-xs text-slate-500 pt-1 border-t border-red-100">
          The system will not resolve this automatically. Decide: <strong>Reject</strong>, <strong>Modify</strong>, promote as a <strong>Revision</strong>, or <strong>Supersede</strong> the existing item.
        </p>
      </div>
    </div>
  );
}

function PromoteDialog({ entry, onClose, onDone }) {
  const claim = entry?.claim;
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("NEW"); // NEW | REVISE | SUPERSEDE
  const [targetId, setTargetId] = useState("");
  const [conflict, setConflict] = useState(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cl = entry?.claim;
    if (!cl) return;
    setType(cl.claim_type);
    setText(cl.claim_text);
    setNote("");
    setResolution("NEW");
    setTargetId("");
    setStatus(cl.claim_type === "OPEN_QUESTION" ? "OPEN" : (["CONSTITUTION", "DECISION"].includes(cl.claim_type) ? "CANONICAL" : "WORKING"));
    // use pre-computed conflict if present, else run radar now
    if (entry.conflict) {
      setConflict(entry.conflict);
      if (entry.conflict.has_conflicts) setTargetId(entry.conflict.conflicts[0].knowledge_id);
    } else {
      setChecking(true);
      api.post(`/claims/${cl.id}/check-conflicts`, {})
        .then((r) => { setConflict(r.data); if (r.data.has_conflicts) setTargetId(r.data.conflicts[0].knowledge_id); })
        .catch(() => {})
        .finally(() => setChecking(false));
    }
  }, [entry]);

  const submit = async () => {
    setBusy(true);
    try {
      let body, data;
      if (resolution === "REVISE") {
        if (!targetId) { toast.error("Choose the item to revise."); setBusy(false); return; }
        ({ data } = await api.post(`/claims/${claim.id}/review`, { action: "REVISE", revise_knowledge_id: targetId, modified_text: text, note }));
        toast.success(`Revised ${data.revised_knowledge_id}. Change recorded immutably.`);
      } else {
        body = { action: "PROMOTE", promote_type: type, promote_status: status, modified_text: text, note };
        if (resolution === "SUPERSEDE") {
          if (!targetId) { toast.error("Choose the item to supersede."); setBusy(false); return; }
          body.supersedes_knowledge_id = targetId;
        }
        ({ data } = await api.post(`/claims/${claim.id}/review`, body));
        toast.success(`Promoted as ${data.promoted_knowledge_id}${data.superseded ? `, superseding ${data.superseded}` : ""}.`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const conflictIds = conflict?.conflicts?.map((c) => c.knowledge_id) || [];

  return (
    <Dialog open={!!claim} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm bg-white max-h-[88vh] overflow-y-auto" data-testid="promote-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Promote to Structured Knowledge</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Contradiction Radar runs automatically. You decide how any conflict is resolved — nothing is overwritten without your action.</DialogDescription>
        </DialogHeader>
        {claim && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <OriginBadge origin={claim.claim_origin} />
              {claim.claim_origin === "AI" && <span className="text-xs text-red-600">AI-origin — promotion requires your explicit founder approval.</span>}
            </div>

            {checking && (
              <div className="flex items-center gap-2 text-sm text-slate-500 border border-slate-200 bg-slate-50 px-3 py-2 rounded-sm" data-testid="promote-conflict-loading">
                <Loader2 size={14} className="animate-spin" /> Contradiction Radar scanning existing knowledge…
              </div>
            )}
            {conflict && <ConflictPanel result={conflict} testid="promote-conflict-panel" />}

            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Statement</Label>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[90px]" data-testid="promote-text" />
            </div>

            {conflict?.has_conflicts && (
              <div className="border border-slate-200 bg-slate-50 p-3 rounded-sm">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Resolution (you decide)</Label>
                <Select value={resolution} onValueChange={setResolution}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-slate-300 bg-white" data-testid="resolution-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="NEW">Promote as NEW item (keep existing)</SelectItem>
                    <SelectItem value="REVISE">Accept as REVISION of existing item</SelectItem>
                    <SelectItem value="SUPERSEDE">SUPERSEDE an existing item</SelectItem>
                  </SelectContent>
                </Select>
                {(resolution === "REVISE" || resolution === "SUPERSEDE") && (
                  <div className="mt-3">
                    <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Target item</Label>
                    <Select value={targetId} onValueChange={setTargetId}>
                      <SelectTrigger className="mt-1.5 rounded-sm border-slate-300 bg-white" data-testid="resolution-target"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="bg-white">{conflictIds.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {resolution === "NEW" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Knowledge Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="promote-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{["CONSTITUTION", "DECISION", "OPEN_QUESTION", "FOUNDER_VIEW", "FACT", "HYPOTHESIS"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="mt-1.5 rounded-sm border-slate-300" data-testid="promote-status"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{["CANONICAL", "WORKING", "OPEN", "HYPOTHESIS"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Title / Reason (optional)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[44px]" data-testid="promote-note" />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="promote-confirm">
              {busy ? "Working…" : resolution === "REVISE" ? "Approve as Revision" : resolution === "SUPERSEDE" ? "Promote & Supersede" : "Approve & Promote"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
